import { Effect } from 'effect';
import type { FileResource } from '@/api/tablesApi';
import { CREATE_CODEMODE_GUIDANCE, PROMPT_FILE_PREVIEW_CHARS } from './create-constants';
import {
  buildProjectDocumentAssetPath,
  buildProjectFileAssetPath,
  inferMimeType,
  truncateForPrompt,
} from './create-file-utils';
import type {
  CreateAsset,
  CreateEnvironmentFileInput,
  CreateProjectAssetResponse,
  CreatePromptAssetPayload,
  CreateRenderAssetPayload,
  CreateSessionFile,
  RenderMetadata,
} from './create-types';

export function assetReferenceLabel(asset: CreateAsset): string {
  if (asset.documentId) return asset.documentId;
  if (asset.fileId) return asset.fileId;
  if (asset.path) return asset.mime ? `${asset.path} (${asset.mime})` : asset.path;
  return asset.error ?? asset.status;
}

export function readySessionDocuments(assets: CreateAsset[]) {
  return assets.flatMap((asset) => {
    if (asset.status !== 'ready' || asset.kind !== 'document' || !asset.documentId) return [];
    return [
      {
        document_id: asset.documentId,
        name: asset.name,
        displayName: asset.name,
      },
    ];
  });
}

export function projectAssetToCreateAsset(asset: CreateProjectAssetResponse): CreateAsset {
  const pathParts = asset.path.split('/');
  const pathName = pathParts[pathParts.length - 1];
  const name = asset.name ?? pathName ?? asset.document_id ?? asset.file_id ?? asset.url ?? 'asset';
  if (asset.source === 'document' && asset.document_id) {
    return {
      localId: asset.id,
      kind: 'document',
      name,
      size: asset.size ?? 0,
      mime: asset.mime,
      status: 'ready',
      documentId: asset.document_id,
      path: asset.path,
    };
  }

  return {
    localId: asset.id,
    kind: 'file',
    name,
    size: asset.size ?? 0,
    mime: asset.mime,
    status: 'ready',
    fileId: asset.file_id,
    path: asset.path,
    content: asset.content,
    encoding: asset.encoding ?? 'utf-8',
  };
}

export function documentProjectAssetInput(params: {
  localId: string;
  file: File;
  documentId: string;
}): CreateEnvironmentFileInput {
  return {
    path: buildProjectDocumentAssetPath(params.documentId, params.file.name),
    name: params.file.name,
    mime: inferMimeType(params.file),
    size: params.file.size,
    document_id: params.documentId,
    metadata: {
      clientAssetId: params.localId,
    },
  };
}

export function inlineProjectAssetInput(file: CreateSessionFile): CreateEnvironmentFileInput {
  return {
    path: file.path,
    name: file.name,
    mime: file.mime,
    size: file.size,
    content: file.content,
    encoding: file.encoding,
    file_id: file.file_id,
    metadata: file.metadata,
  };
}

export function fileProjectAssetInput(params: {
  localId: string;
  file: FileResource;
}): CreateEnvironmentFileInput {
  return {
    path: buildProjectFileAssetPath(params.file.id, params.file.name),
    name: params.file.name,
    mime: params.file.mime,
    size: params.file.size,
    sha256: params.file.sha256,
    file_id: params.file.id,
    metadata: {
      clientAssetId: params.localId,
      source: 'files',
    },
  };
}

export function readyCreateSessionFiles(assets: CreateAsset[]): CreateSessionFile[] {
  return assets.flatMap((asset) => {
    if (asset.status !== 'ready' || asset.kind !== 'file' || !asset.path || !asset.mime) {
      return [];
    }
    if (!asset.fileId && (!asset.encoding || typeof asset.content !== 'string')) return [];
    return [
      {
        path: asset.path,
        name: asset.name,
        mime: asset.mime,
        size: asset.size,
        ...(asset.content !== undefined ? { content: asset.content } : {}),
        ...(asset.encoding ? { encoding: asset.encoding } : {}),
        ...(asset.fileId ? { file_id: asset.fileId } : {}),
        metadata: {
          clientAssetId: asset.localId,
          ...(asset.fileId ? { source: 'files' } : {}),
        },
      },
    ];
  });
}

export function readyRenderAssetPayload(assets: CreateAsset[]): CreateRenderAssetPayload[] {
  const payload: CreateRenderAssetPayload[] = [];
  for (const asset of assets) {
    if (asset.status !== 'ready') continue;
    if (asset.kind === 'document' && asset.documentId) {
      payload.push({
        kind: 'document',
        name: asset.name,
        size: asset.size,
        mime: asset.mime,
        documentId: asset.documentId,
        document_id: asset.documentId,
      });
      continue;
    }
    if (
      asset.kind === 'file' &&
      asset.path &&
      asset.mime &&
      (asset.fileId || (asset.encoding && typeof asset.content === 'string'))
    ) {
      payload.push({
        kind: 'file',
        path: asset.path,
        name: asset.name,
        mime: asset.mime,
        size: asset.size,
        ...(asset.content !== undefined ? { content: asset.content } : {}),
        ...(asset.encoding ? { encoding: asset.encoding } : {}),
        ...(asset.fileId ? { file_id: asset.fileId } : {}),
        metadata: {
          clientAssetId: asset.localId,
          ...(asset.fileId ? { source: 'files' } : {}),
        },
      });
    }
  }
  return payload;
}

export function readyPromptAssetPayload(assets: CreateAsset[]): CreatePromptAssetPayload[] {
  const payload: CreatePromptAssetPayload[] = [];
  for (const asset of assets) {
    if (asset.status !== 'ready') continue;
    if (asset.kind === 'document' && asset.documentId) {
      payload.push({
        kind: 'document',
        name: asset.name,
        size: asset.size,
        mime: asset.mime,
        documentId: asset.documentId,
        document_id: asset.documentId,
      });
      continue;
    }
    if (asset.kind === 'file' && asset.path && asset.mime && (asset.fileId || asset.encoding)) {
      const preview =
        asset.encoding === 'utf-8' && typeof asset.content === 'string'
          ? truncateForPrompt(asset.content, PROMPT_FILE_PREVIEW_CHARS)
          : null;
      payload.push({
        kind: 'file',
        path: asset.path,
        name: asset.name,
        mime: asset.mime,
        size: asset.size,
        ...(asset.encoding ? { encoding: asset.encoding } : {}),
        ...(asset.fileId ? { file_id: asset.fileId } : {}),
        metadata: {
          clientAssetId: asset.localId,
          ...(asset.fileId ? { source: 'files' } : {}),
        },
        session_content_available: typeof asset.content === 'string',
        ...(preview
          ? {
              content_preview: preview.text,
              content_preview_truncated: preview.truncated,
            }
          : {
              binary_content_omitted: true,
            }),
      });
    }
  }
  return payload;
}

export function getStringField(value: unknown, key: string): string | null {
  if (!value || typeof value !== 'object') return null;
  const field = (value as Record<string, unknown>)[key];
  return typeof field === 'string' && field.trim() ? field.trim() : null;
}

export function getNumberField(value: unknown, key: string): number | null {
  if (!value || typeof value !== 'object') return null;
  const field = (value as Record<string, unknown>)[key];
  return typeof field === 'number' && Number.isFinite(field) ? field : null;
}

export const metadataFromRenderOutputEffect = Effect.fn(
  'web.createAssets.metadataFromRenderOutput',
)(function* (output: unknown) {
  const documentId = getStringField(output, 'documentId') ?? getStringField(output, 'document_id');
  if (!documentId) return null;

  return {
    documentId,
    renderId: getStringField(output, 'renderId') ?? getStringField(output, 'render_id'),
    fileName:
      getStringField(output, 'fileName') ??
      getStringField(output, 'file_name') ??
      'okra-create-output.pdf',
    sha256: getStringField(output, 'sha256'),
    size: getNumberField(output, 'size'),
    publicUrl:
      getStringField(output, 'publicUrl') ??
      getStringField(output, 'public_url') ??
      getStringField(output, 'publishedUrl') ??
      getStringField(output, 'published_url'),
    publishBaseUrl:
      getStringField(output, 'publishBaseUrl') ?? getStringField(output, 'publish_base_url'),
    sourceUrl: getStringField(output, 'sourceUrl') ?? getStringField(output, 'source_url'),
  };
});

export function metadataFromRenderOutput(output: unknown): RenderMetadata | null {
  return Effect.runSync(metadataFromRenderOutputEffect(output));
}

export function renderArtifactId(metadata: RenderMetadata): string {
  return metadata.renderId || metadata.documentId || metadata.sha256 || metadata.fileName;
}

export function buildAgentPrompt(params: {
  prompt: string;
  code: string;
  assets: CreateAsset[];
}): string {
  const assets = readyPromptAssetPayload(params.assets);
  return [
    'Create workspace turn.',
    '',
    `User request: ${params.prompt}`,
    '',
    `Uploaded assets JSON:\n${JSON.stringify(assets, null, 2)}`,
    '',
    `Renderer contract:\n${CREATE_CODEMODE_GUIDANCE}`,
    '',
    params.code.trim()
      ? `Current codemode:\n\`\`\`js\n${params.code.trim()}\n\`\`\``
      : 'Current codemode: none',
    '',
    'If the user is asking for a PDF output, call render_image_pdf with a complete final prompt and concise context. Parsed PDFs stay available through doc_sql; session-scoped non-PDF file contents can be read with read_create_asset. Do not publish from the agent turn unless the user explicitly asks to publish.',
  ].join('\n');
}
